import { createContext } from 'preact';
import { useCallback, useContext, useEffect, useState } from 'preact/hooks';
import type { Repository } from '../data/repository';

const RepositoryContext = createContext<Repository | null>(null);

export const RepositoryProvider = RepositoryContext.Provider;

export function useRepository(): Repository {
  const repo = useContext(RepositoryContext);
  if (repo === null) {
    throw new Error('useRepository must be used inside a RepositoryProvider');
  }
  return repo;
}

export interface AsyncState<T> {
  readonly data: T | undefined;
  readonly error: Error | undefined;
  readonly loading: boolean;
  readonly reload: () => void;
}

/**
 * Minimal async data hook.
 *
 * There is no cache and no request dedupe: this is a single-user local database
 * where every read is an IndexedDB hit measured in single-digit milliseconds.
 * Mutations call `reload` on the affected view rather than invalidating a store,
 * which keeps the data flow obvious at this size.
 */
export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .then((value) => {
        if (!cancelled) {
          setData(value);
          setError(undefined);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause : new Error(String(cause)));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [...deps, nonce]);

  return { data, error, loading, reload };
}
